import { Picture } from "../../image/src/image";
import { css } from "@compiled/react";

import { useIntl } from "react-intl";
import { GuardLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives/responsive";

import { token } from "@atlaskit/tokens";
import { Link } from "@/libraries/design/components";

import { wacToken } from "@/libraries/design/foundations";

export interface AtlassianGuardAddOnProps {
    headingText?: string | null;
    bodyText?: string | null;
    linkText?: string | null;
    ctaHref: string;
}

const containerCss = css({
    "maxWidth": 968,
    "margin": "0 auto",
    "padding": token("space.600"),
    "display": "flex",
    "borderRadius": wacToken("corner.radius.card"),
    "alignItems": "center",
    [media.below.sm]: {
        flexDirection: "column",
        gap: 21,
        paddingBlock: token("space.400"),
        paddingInline: token("space.300")
    },
    "boxShadow": token("elevation.shadow.raised"),
    "transition": "box-shadow 0.2s ease-in-out",
    ":hover": {
        boxShadow: token("elevation.shadow.overlay")
    },
    "gap": token("space.600"),
    "cursor": "pointer"
});

const sectionRightCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200")
});

const atlassianGuardLogoContainerCss = css({
    "height": token("space.400"),
    // Note: Existing tech debt, should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& svg": {
        height: token("space.400")
    }
});

const headingTextCss = css({
    fontSize: 24,
    fontWeight: 700,
    lineHeight: "30px",
    margin: 0
});

const bodyTextCss = css({
    margin: 0,
    color: wacToken("color.neutral.dark500")
});

const svgStyles = css({
    width: "91px",
    height: "78.3px",
    [media.above.md]: {
        width: "155.19px",
        height: "133.73px"
    }
});

export const GuardShieldImage = () => {
    return (
        <Picture
            image={{
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/xxlo4l61p3fn223u1nahuu4r6r3ug1a2.svg",
                alt: ""
            }}
            css={svgStyles}
        />
    );
};

export const AtlassianGuardAddOnShared = ({
    headingText,
    bodyText,
    linkText,
    ctaHref
}: AtlassianGuardAddOnProps) => {
    const { formatMessage } = useIntl();
    const defaultHeadingText = formatMessage({
        id: "PricingPagesTeam.atlassianGuard.heading",
        defaultMessage: "Safeguard your mission-critical data"
    });

    const defaultBodyText = formatMessage({
        id: "PricingPagesTeam.atlassianGuard.body",
        defaultMessage:
            "Add Atlassian Guard for enhanced security and visibility across your Atlassian cloud products. Set proactive security policies, quickly detect suspicious behavior, and take action with suggested remediation steps to protect your most valuable work. Additionally, with a Cloud Enterprise plan, you get support for multiple identity providers (IdPs) and Atlassian Guard Standard."
    });

    const defaultLinkText = formatMessage({
        id: "PricingPages.atlassianGuard.cta",
        defaultMessage: "Learn more"
    });

    const handleClick = () => {
        if (ctaHref) {
            window.location.href = ctaHref;
        }
    };

    return (
        <div
            data-testid="atlassianGuardAddOnShared"
            css={containerCss}
            tabIndex={0}
            onClick={handleClick}
            role="link"
            onKeyDown={(e) => {
                if (e.code == "Enter" || e.code == "Space") {
                    e.preventDefault();
                    handleClick();
                }
            }}
        >
            <GuardShieldImage />
            <div css={sectionRightCss}>
                <span css={atlassianGuardLogoContainerCss}>
                    <GuardLogo appearance="brand" />
                </span>
                <h4 css={headingTextCss} id="atlassian-guard-add-on-heading">
                    {headingText ?? defaultHeadingText}
                </h4>

                {bodyText !== "" && (
                    <p css={bodyTextCss}>{bodyText ?? defaultBodyText}</p>
                )}

                <Link
                    text={linkText ?? defaultLinkText}
                    type="primary"
                    href={ctaHref}
                    id="atlassian-guard-add-on-cta"
                    ariaLabelledby="atlassian-guard-add-on-cta atlassian-guard-add-on-heading"
                />
            </div>
        </div>
    );
};

AtlassianGuardAddOnShared.displayName = "AtlassianGuardAddOn";
